/**
 * Parses knowledge-base citation values emitted by the agents service.
 *
 * The only supported format is an S3 URI shaped like:
 *
 *   s3://<bucket>/.files-kb-<accountId>/<projectId>/<file-service-path>[?page=N]
 *
 * `<file-service-path>` is the file's `name` in the LifeOmic file service
 * (project-scoped). The browser resolves the file id via a name+datasetId
 * lookup at click-time; nothing about a file UUID is encoded in the citation.
 */

import QueryString from 'qs';
import type { KnowledgeBaseCitation } from '../../types';

const S3_KB_URI_RE = /^s3:\/\/[^/]+\/\.files-kb-([^/]+)\/([^/]+)\/(.+)$/;

const parsePagePositiveInt = (
  query: string | undefined,
): number | undefined => {
  if (!query) {
    return undefined;
  }
  const parsed = QueryString.parse(query);
  const raw = Array.isArray(parsed.page) ? parsed.page[0] : parsed.page;
  if (typeof raw !== 'string' || !/^\d+$/.test(raw)) {
    return undefined;
  }
  const value = parseInt(raw, 10);
  return value > 0 ? value : undefined;
};

export const parseKnowledgeBaseCitation = (
  reference: string,
): KnowledgeBaseCitation | null => {
  const trimmed = reference.trim();
  if (!trimmed) {
    return null;
  }

  const queryIndex = trimmed.indexOf('?');
  const base = queryIndex === -1 ? trimmed : trimmed.slice(0, queryIndex);
  const query = queryIndex === -1 ? undefined : trimmed.slice(queryIndex + 1);

  const match = S3_KB_URI_RE.exec(base);
  if (!match) {
    return null;
  }
  const [, accountId, projectId, name] = match;
  if (!accountId || !projectId || !name) {
    return null;
  }

  const displayName = name.split('/').pop() || name;
  const page = parsePagePositiveInt(query);

  return {
    citationType: 'KnowledgeBase',
    accountId,
    projectId,
    name,
    displayName,
    sourceLabel: 'Knowledge base',
    page,
    title: `${displayName} — Knowledge base`,
  };
};

/**
 * Mimes the UI opens in a new tab via file-service (browser-native preview).
 * Other mimes use a download-link fallback.
 */
export const isBrowserPreviewableKnowledgeBaseMime = (
  mime: string | undefined,
): boolean => {
  if (!mime) {
    return true;
  }
  const m = mime.toLowerCase();
  return (
    m === 'application/pdf' || m.startsWith('image/') || m.startsWith('text/')
  );
};
